/**
 * Position Randomization Utility for Race Animation
 *
 * This utility provides functions to randomize competitor positions while preserving
 * winner mapping and maintaining race integrity. Uses Fisher-Yates shuffle algorithm
 * for fair randomization.
 */

// Interface for tracking original vs randomized positions
export interface PositionMapping {
  competitorId: string;
  originalIndex: number;
  randomizedIndex: number;
  lanePosition: LanePosition;
}

// Lane position interface matching RaceTrack component
export interface LanePosition {
  laneY: number;
  horseY: number;
}

// Generic competitor interface for randomization
export interface CompetitorForRandomization {
  id: string;
  name: string;
  [key: string]: unknown; // Allow additional properties
}

// Configuration options for randomization
export interface RandomizationConfig {
  preserveWinnerAdvantage?: boolean;
  allowWinnerInLastLane?: boolean;
  minimumPositionVariation?: number;
}

/**
 * Fisher-Yates shuffle algorithm implementation
 * Provides fair randomization with uniform distribution
 */
function fisherYatesShuffle<T>(array: T[]): T[] {
  const shuffled = [...array]; // Create a copy to avoid mutating original

  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled;
}

/**
 * Creates position mappings for competitors with randomized lane assignments
 */
export function createPositionMappings<T extends CompetitorForRandomization>(
  competitors: T[],
  lanePositions: LanePosition[],
  winnerId?: string,
  _config: RandomizationConfig = {}
): {
  randomizedCompetitors: T[];
  positionMappings: PositionMapping[];
} {
  if (competitors.length === 0) {
    return {
      randomizedCompetitors: [],
      positionMappings: [],
    };
  }

  // Create array of indices to shuffle
  const indices = Array.from({ length: competitors.length }, (_, i) => i);
  const shuffledIndices = fisherYatesShuffle(indices);

  // Create randomized competitor array and position mappings
  const randomizedCompetitors: T[] = [];
  const positionMappings: PositionMapping[] = [];

  shuffledIndices.forEach((originalIndex, newIndex) => {
    const competitor = competitors[originalIndex];
    randomizedCompetitors.push(competitor);

    positionMappings.push({
      competitorId: competitor.id,
      originalIndex,
      randomizedIndex: newIndex,
      lanePosition: lanePositions[newIndex] || { laneY: 0, horseY: 0 },
    });
  });

  return {
    randomizedCompetitors,
    positionMappings,
  };
}
/**
 * Shuffles competitor positions while preserving winner information
 * This is the main function that should be used by the RaceTrack component
 */
export function shuffleCompetitorPositions<
  T extends CompetitorForRandomization
>(
  competitors: T[],
  lanePositions: LanePosition[],
  winnerId?: string,
  config: RandomizationConfig = {}
): {
  shuffledCompetitors: T[];
  positionMappings: PositionMapping[];
  winnerMapping?: PositionMapping;
} {
  const { randomizedCompetitors, positionMappings } = createPositionMappings(
    competitors,
    lanePositions,
    winnerId,
    config
  );

  // Find winner mapping if winner is specified
  const winnerMapping = winnerId
    ? positionMappings.find((mapping) => mapping.competitorId === winnerId)
    : undefined;

  return {
    shuffledCompetitors: randomizedCompetitors,
    positionMappings,
    winnerMapping,
  };
}

/**
 * Gets the randomized position for a specific competitor
 */
export function getRandomizedPosition(
  competitorId: string,
  positionMappings: PositionMapping[]
): PositionMapping | undefined {
  return positionMappings.find(
    (mapping) => mapping.competitorId === competitorId
  );
}

/**
 * Gets the original position for a competitor given their randomized position
 */
export function getOriginalPosition(
  randomizedIndex: number,
  positionMappings: PositionMapping[]
): PositionMapping | undefined {
  return positionMappings.find(
    (mapping) => mapping.randomizedIndex === randomizedIndex
  );
}

/**
 * Validates that position mappings are consistent and complete
 */
export function validatePositionMappings(
  competitors: CompetitorForRandomization[],
  positionMappings: PositionMapping[]
): boolean {
  // Check that we have mappings for all competitors
  if (competitors.length !== positionMappings.length) {
    return false;
  }

  // Check that all competitor IDs are present
  const competitorIds = new Set(competitors.map((c) => c.id));
  const mappingIds = new Set(positionMappings.map((m) => m.competitorId));

  if (competitorIds.size !== mappingIds.size) {
    return false;
  }

  for (const id of competitorIds) {
    if (!mappingIds.has(id)) {
      return false;
    }
  }

  // Check that randomized indices are unique and within bounds
  const randomizedIndices = positionMappings.map((m) => m.randomizedIndex);
  const uniqueIndices = new Set(randomizedIndices);

  if (uniqueIndices.size !== randomizedIndices.length) {
    return false;
  }

  for (const index of randomizedIndices) {
    if (index < 0 || index >= competitors.length) {
      return false;
    }
  }

  return true;
}
